// src/guards/AuthGuard.tsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireAdmin?: boolean;
}

const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requireAuth = true,
  requireAdmin = false,
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = React.useState<boolean | null>(null);
  const [checkingAdmin, setCheckingAdmin] = React.useState(requireAdmin);

  React.useEffect(() => {
    if (requireAdmin && user) {
      supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single()
        .then(({ data, error }) => {
          if (!error && data?.role === 'admin') {
            setIsAdmin(true);
          } else {
            setIsAdmin(false);
          }
        })
        .catch(() => setIsAdmin(false))
        .finally(() => setCheckingAdmin(false));
    } else {
      setIsAdmin(false);
      setCheckingAdmin(false);
    }
  }, [requireAdmin, user]);

  // Gestion du lien de récupération
  const isRecoveryLink = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get('type') === 'recovery' ||
      window.location.hash.includes('type=recovery')
    );
  }, []);

  if (loading) return null;

  if (isRecoveryLink) {
    if (location.pathname === '/reset-password') return <>{children}</>;
    if (user) return <Navigate to="/settings?type=recovery" replace />;
    return <Navigate to="/reset-password" replace />;
  }

  if (!requireAuth && user) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireAuth && !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireAdmin && checkingAdmin) {
    return null;
  }

  if (requireAdmin && isAdmin === false) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default AuthGuard;
